import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { handleApiBadges } from "@/lib/badges";
import { Plus, Clock, Trash2, Video, Pencil } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarSyncButton } from "@/components/CalendarSyncDialog";

function detectProvider(link) {
  if (!link) return "jitsi";
  if (link.includes("meet.google.com")) return "google";
  if (link.includes("zoom.us")) return "zoom";
  if (link.includes("jit.si")) return "jitsi";
  return "jitsi";
}

function providerLabel(link) {
  const p = detectProvider(link);
  if (p === "google") return "Google Meet";
  if (p === "zoom") return "Zoom";
  return "Jitsi";
}

export default function CoachCalendar() {
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [form, setForm] = useState({ student_id: "", title: "", date: "", time: "10:00", duration: 60, notes: "", meeting_link: "", auto_meeting: true, meeting_provider: "jitsi" });
  const [editForm, setEditForm] = useState({ title: "", time: "10:00", duration: 60, notes: "", meeting_link: "", auto_meeting: false, meeting_provider: "jitsi" });
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const fetchData = () => {
    api.get('/coaching-sessions').then(r => setSessions(r.data));
    api.get('/students').then(r => setStudents(r.data));
  };
  useEffect(() => { fetchData(); }, []);

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const visibleSessions = sessions.filter(s => s.status !== "cancelled");
  const sessionDates = visibleSessions.map(s => s.date);
  const selectedDateStr = formatDate(selectedDate);
  const daySessions = visibleSessions.filter(s => s.date === selectedDateStr);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/coaching-sessions', { ...form, date: selectedDateStr });
      handleApiBadges(res, toast);
      toast.success("Séance créée");
      setOpen(false);
      setForm({ student_id: "", title: "", date: "", time: "10:00", duration: 60, notes: "", meeting_link: "", auto_meeting: true, meeting_provider: "jitsi" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/coaching-sessions/${id}`);
    toast.success("Séance supprimée");
    fetchData();
  };

  const handleStatusUpdate = async (id, status) => {
    const res = await api.put(`/coaching-sessions/${id}`, { status });
    handleApiBadges(res, toast);
    toast.success("Statut mis à jour");
    fetchData();
  };

  const openEdit = (session) => {
    setEditingSession(session);
    setEditForm({
      title: session.title || "",
      time: session.time || "10:00",
      duration: session.duration || 60,
      notes: session.notes || "",
      meeting_link: session.meeting_link || "",
      auto_meeting: false,
      meeting_provider: detectProvider(session.meeting_link),
    });
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingSession) return;
    setEditLoading(true);
    try {
      const payload = {
        title: editForm.title,
        time: editForm.time,
        duration: editForm.duration,
        notes: editForm.notes,
        meeting_link: editForm.auto_meeting ? "" : editForm.meeting_link,
        auto_meeting: editForm.auto_meeting,
        meeting_provider: editForm.meeting_provider,
      };
      await api.put(`/coaching-sessions/${editingSession.session_id}`, payload);
      toast.success("Séance mise à jour");
      setEditOpen(false);
      setEditingSession(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div data-testid="coach-calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Calendrier</h1>
          <p className="text-slate-500 mt-1">{sessions.filter(s => s.status === 'scheduled').length} séance(s) prévue(s)</p>
        </div>
        <div className="flex gap-2">
          <CalendarSyncButton accent="blue" />
          <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="create-session-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle séance
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[340px,1fr] gap-6 mt-6">
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ hasSession: sessionDates.map(d => new Date(d + 'T00:00:00')) }}
              modifiersClassNames={{ hasSession: "bg-blue-100 text-blue-700 font-semibold" }}
              className="rounded-xl"
              data-testid="calendar-widget"
            />
          </CardContent>
        </Card>

        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
            Séances du {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h3>
          {daySessions.length === 0 ? (
            <p className="text-slate-400 text-sm py-8 text-center">Aucune séance ce jour</p>
          ) : (
            <div className="space-y-3">
              {daySessions.map(s => (
                <Card key={s.session_id} className="border-slate-100" data-testid={`session-${s.session_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">{s.title}</h4>
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.time}</span>
                          <span>{s.duration}min</span>
                          <span>{s.student_name || "N/A"}</span>
                        </div>
                        {s.notes && <p className="text-sm text-slate-400 mt-2">{s.notes}</p>}
                        {s.meeting_link && (
                          <a href={s.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">
                            <Video className="w-3.5 h-3.5" /> Rejoindre ({providerLabel(s.meeting_link)})
                          </a>
                        )}
                        <Badge className="mt-2" variant={s.status === 'completed' ? 'default' : s.status === 'cancelled' ? 'destructive' : 'secondary'}>
                          {s.status === 'scheduled' ? 'Prévue' : s.status === 'completed' ? 'Terminée' : 'Annulée'}
                        </Badge>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(s)} title="Modifier">
                          <Pencil className="w-4 h-4 text-blue-500" />
                        </Button>
                        {s.status === 'scheduled' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleStatusUpdate(s.session_id, 'completed')} className="text-xs text-green-600">Terminer</Button>
                            <Button variant="ghost" size="sm" onClick={() => handleStatusUpdate(s.session_id, 'cancelled')} className="text-xs text-red-400">Annuler</Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(s.session_id)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Modifier la séance</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div><Label>Titre</Label><Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} required className="mt-1.5 h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Heure</Label><Input type="time" value={editForm.time} onChange={e => setEditForm({ ...editForm, time: e.target.value })} className="mt-1.5 h-11" /></div>
              <div><Label>Durée (min)</Label><Input type="number" value={editForm.duration} onChange={e => setEditForm({ ...editForm, duration: parseInt(e.target.value) })} className="mt-1.5 h-11" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1.5" /></div>
            <div className="flex items-center gap-2">
              <Checkbox id="edit_auto_meeting" checked={editForm.auto_meeting} onCheckedChange={(v) => setEditForm({ ...editForm, auto_meeting: !!v })} />
              <Label htmlFor="edit_auto_meeting" className="text-sm font-normal cursor-pointer">Générer / régénérer lien visio</Label>
            </div>
            {editForm.auto_meeting && (
              <div>
                <Label>Plateforme visio</Label>
                <Select value={editForm.meeting_provider} onValueChange={v => setEditForm({ ...editForm, meeting_provider: v })}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jitsi">Jitsi Meet</SelectItem>
                    <SelectItem value="google">Google Meet</SelectItem>
                    <SelectItem value="zoom">Zoom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {!editForm.auto_meeting && (
              <div>
                <Label>Lien visio</Label>
                <Input
                  value={editForm.meeting_link}
                  onChange={e => setEditForm({ ...editForm, meeting_link: e.target.value })}
                  placeholder="https://meet.jit.si/..."
                  className="mt-1.5 h-11"
                />
                {editForm.meeting_link && (
                  <a href={editForm.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">
                    <Video className="w-3.5 h-3.5" /> Tester le lien
                  </a>
                )}
              </div>
            )}
            {editingSession && (
              <p className="text-sm text-slate-500">
                Date: {new Date(`${editingSession.date}T00:00:00`).toLocaleDateString('fr-FR')} · {editingSession.student_name}
              </p>
            )}
            <Button type="submit" disabled={editLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11">
              {editLoading ? "Enregistrement..." : "Enregistrer les modifications"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Nouvelle séance</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div>
              <Label>Élève</Label>
              <Select value={form.student_id} onValueChange={v => setForm({ ...form, student_id: v })}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="session-student-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{students.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Titre</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1.5 h-11" data-testid="session-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Heure</Label><Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="mt-1.5 h-11" /></div>
              <div><Label>Durée (min)</Label><Input type="number" value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) })} className="mt-1.5 h-11" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
            <div className="flex items-center gap-2">
              <Checkbox id="auto_meeting" checked={form.auto_meeting} onCheckedChange={(v) => setForm({ ...form, auto_meeting: !!v })} />
              <Label htmlFor="auto_meeting" className="text-sm font-normal cursor-pointer">Générer un lien visio automatiquement</Label>
            </div>
            {form.auto_meeting && (
              <div>
                <Label>Plateforme visio</Label>
                <Select value={form.meeting_provider} onValueChange={v => setForm({ ...form, meeting_provider: v })}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jitsi">Jitsi Meet</SelectItem>
                    <SelectItem value="google">Google Meet</SelectItem>
                    <SelectItem value="zoom">Zoom</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">
                  {form.meeting_provider === "jitsi" && "Salle Jitsi dédiée à la séance"}
                  {form.meeting_provider === "google" && "Lien Google Meet (nouvelle réunion au clic)"}
                  {form.meeting_provider === "zoom" && "Lien Zoom (nouvelle réunion au clic)"}
                </p>
              </div>
            )}
            {!form.auto_meeting && (
              <div><Label>Lien visio personnalisé</Label><Input value={form.meeting_link} onChange={e => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://meet.google.com/..." className="mt-1.5 h-11" /></div>
            )}
            <p className="text-sm text-slate-500">Date: {selectedDate.toLocaleDateString('fr-FR')}</p>
            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11" data-testid="submit-session-btn">
              {loading ? "Création..." : "Créer la séance"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
